import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Button,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { useSearchParams } from "react-router-dom";
import { OpenSourceStatus } from "./utils/OpenSourceUtils";

/**
 * Component for filtering AI models
 */
const AiModelFilter = ({ models, onFilterChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract unique values for filter options
  const architectures = [
    ...new Set(
      models.flatMap((model) =>
        model.model_architecture
          ? model.model_architecture.map((arch) => arch.name)
          : []
      )
    ),
  ].sort();

  const developers = [
    ...new Set(
      models
        .filter((model) => model.model_owner)
        .map((model) => model.model_owner)
    ),
  ].sort();

  const sourceTypes = [
    ...new Set(
      models
        .filter((model) => model.source_type)
        .map((model) => model.source_type)
    ),
  ].sort();

  // Initialize state from URL params - using a function to ensure it only runs once
  const [filters, setFilters] = useState(() => {
    return {
      architecture: searchParams.getAll("architecture") || [],
      developer: searchParams.getAll("developer") || [],
      specialization: searchParams.getAll("specialization") || [],
      sourceType: searchParams.getAll("sourceType") || [],
    };
  });

  // Track if this is the first render to avoid updating URL params on initial load
  const [initialRender, setInitialRender] = useState(true);

  // Update URL when filters change
  useEffect(() => {
    // Skip the first render to prevent an unnecessary URL update
    if (initialRender) {
      setInitialRender(false);
      return;
    }

    const params = new URLSearchParams();

    filters.architecture.forEach((arch) => params.append("architecture", arch));
    filters.developer.forEach((dev) => params.append("developer", dev));
    filters.specialization.forEach((spec) =>
      params.append("specialization", spec)
    );
    filters.sourceType.forEach((src) => params.append("sourceType", src));

    // Only update URL params if they've actually changed
    const currentParams = new URLSearchParams(searchParams);
    if (params.toString() !== currentParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, setSearchParams, initialRender, searchParams]);

  // Apply filters to models (separate from URL updates)
  useEffect(() => {
    const filteredModels = models.filter((model) => {
      // Check architecture filter
      if (filters.architecture.length > 0) {
        const modelArchs =
          model.model_architecture?.map((arch) => arch.name) || [];
        if (!filters.architecture.some((arch) => modelArchs.includes(arch))) {
          return false;
        }
      }

      // Check developer filter
      if (
        filters.developer.length > 0 &&
        !filters.developer.includes(model.model_owner)
      ) {
        return false;
      }

      // Check specialization filter
      if (filters.specialization.length > 0) {
        const isLegal = model.legal_specific ? "legal" : null;
        const isPatent = model.patent_specific ? "patent" : null;
        const modelSpecs = [isLegal, isPatent].filter(Boolean);
        if (modelSpecs.length === 0) {
          modelSpecs.push("general");
        }
        if (!filters.specialization.some((spec) => modelSpecs.includes(spec))) {
          return false;
        }
      }

      // Check source type filter
      if (
        filters.sourceType.length > 0 &&
        !filters.sourceType.includes(model.source_type)
      ) {
        return false;
      }

      return true;
    });

    onFilterChange(filteredModels);
  }, [filters, models, onFilterChange]);

  // Handle filter changes
  const handleFilterChange = (event, filterType) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      architecture: [],
      developer: [],
      specialization: [],
      sourceType: [],
    });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <FilterListIcon /> Filter AI Models
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearAllIcon />}
            onClick={handleClearFilters}
            disabled={!Object.values(filters).some((arr) => arr.length > 0)}
          >
            Clear All
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {/* Architecture Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }} size="small">
            <InputLabel id="architecture-filter-label">Architecture</InputLabel>
            <Select
              labelId="architecture-filter-label"
              id="architecture-filter"
              multiple
              value={filters.architecture}
              onChange={(e) => handleFilterChange(e, "architecture")}
              input={<OutlinedInput label="Architecture" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {architectures.map((arch) => (
                <MenuItem key={arch} value={arch}>
                  {arch}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Developer Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }} size="small">
            <InputLabel id="developer-filter-label">Provider</InputLabel>
            <Select
              labelId="developer-filter-label"
              id="developer-filter"
              multiple
              value={filters.developer}
              onChange={(e) => handleFilterChange(e, "developer")}
              input={<OutlinedInput label="Provider" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {developers.map((dev) => (
                <MenuItem key={dev} value={dev}>
                  {dev}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Specialization Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }} size="small">
            <InputLabel id="specialization-filter-label">
              Specialization
            </InputLabel>
            <Select
              labelId="specialization-filter-label"
              id="specialization-filter"
              multiple
              value={filters.specialization}
              onChange={(e) => handleFilterChange(e, "specialization")}
              input={<OutlinedInput label="Specialization" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={
                        value === "legal"
                          ? "Legal"
                          : value === "patent"
                          ? "Patent"
                          : "General"
                      }
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="legal">Legal</MenuItem>
              <MenuItem value="patent">Patent</MenuItem>
              <MenuItem value="general">General Purpose</MenuItem>
            </Select>
          </FormControl>

          {/* Source Type Filter */}
          <FormControl sx={{ minWidth: 200, flex: 1 }} size="small">
            <InputLabel id="source-type-filter-label">Open Source</InputLabel>
            <Select
              labelId="source-type-filter-label"
              id="source-type-filter"
              multiple
              value={filters.sourceType}
              onChange={(e) => handleFilterChange(e, "sourceType")}
              input={<OutlinedInput label="Open Source" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => {
                    const status = OpenSourceStatus.getStatus(value);
                    return (
                      <Chip
                        key={value}
                        label={status.label}
                        size="small"
                        color={status.color}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {sourceTypes.map((type) => {
                const status = OpenSourceStatus.getStatus(type);
                return (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {status.icon}
                      {status.label}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Active filters summary */}
        {Object.values(filters).some((arr) => arr.length > 0) && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Active Filters:{" "}
              {Object.values(filters).reduce(
                (acc, curr) => acc + curr.length,
                0
              )}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AiModelFilter;
